import { createContext } from 'react';

export enum ProfileError {
    UNKNOWN_ERROR,
    SERVER_ERROR,
    NO_PERMISSION,
    REQUEST_ABORTED,
}

export type License = {
    key: string;
    name: string;
    configs?: any;
    taskConfig?: any;
    distributionInfo?: any;
    createdAt?: any;
};

type ProfileContextType = {
    licenses: { [x: string]: License };
    licensesLoaded: boolean;
    errors: ProfileError[];
    getLicensesFromToken: () => Promise<void>;
};

const ProfileContext = createContext<ProfileContextType>({
    licenses: {},
    licensesLoaded: false,
    errors: [],
    getLicensesFromToken: () => Promise.resolve(),
});

export const getProfileErrorText = (profileError: ProfileError) => {
    const messages = new Map([
        [ProfileError.UNKNOWN_ERROR, 'Unknown error occurred. Please try again later.'],
        [ProfileError.SERVER_ERROR, 'Server error occurred.'],
        [
            ProfileError.NO_PERMISSION,
            'You do not have permission to access the Developer Portal. Please contact STREAMSIX for access.',
        ],
        [
            ProfileError.REQUEST_ABORTED,
            'Request took too long. Please refresh the page or try again later!',
        ],
    ]);

    return `${messages.get(profileError)} Error code: ${profileError}`;
};

export const getProfileErrorHeader = (profileError: ProfileError) => {
    const headers = new Map([
        [ProfileError.UNKNOWN_ERROR, 'UNKNOWN ERROR'],
        [ProfileError.SERVER_ERROR, 'SERVER ERROR'],
        [ProfileError.NO_PERMISSION, 'ACCESS DENIED'],
        [ProfileError.REQUEST_ABORTED, 'REQUEST ABORTED'],
    ]);
    return `${headers.get(profileError)}`;
};

export default ProfileContext;

interface ProfileContextProviderProps {
    licenses: { [x: string]: License };
    licensesLoaded: boolean;
    errors: ProfileError[];
    getLicensesFromToken: () => Promise<void>;
}

export const ProfileContextProvider: React.FC<ProfileContextProviderProps> = ({
    children,
    licenses,
    licensesLoaded,
    errors,
    getLicensesFromToken,
}) => {
    return (
        <ProfileContext.Provider
            value={{
                licenses,
                licensesLoaded,
                errors,
                getLicensesFromToken,
            }}
        >
            {children}
        </ProfileContext.Provider>
    );
};
